<script setup>
import {HpoPredefinedDiagnosisListQuery} from "@/graphql/queries/hpo/diagnosis.graphql";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {mdiCloseCircle} from "@mdi/js";
import {pgettext} from "@/utils/translate";
import DiagnosisBuilder from "@/components/hpo/patientModal/diagnosis/DiagnosisBuilder.vue";
import {computed, ref, useTemplateRef, watch} from "vue";

const diagnosis = defineModel({type: String});
const props = defineProps({
    fieldProps: {
        type: Object,
    },
});
const emit = defineEmits(['submit']);

const builder = useTemplateRef('builder');

const {result, loading, refetch} = useQuery(HpoPredefinedDiagnosisListQuery);
const predefinedDiagnosisList = useLastResult(result, null);

const showErrorDialog = ref(false);
function submit() {
    const diagnosisResult = builder.value?.diagnosisObj?.toString();
    diagnosis.value = diagnosisResult;
    if (diagnosisResult) {
        emit('submit', diagnosisResult);
    } else {
        showErrorDialog.value = true;
    }
}
</script>

<template>
    <main>
        <header class="d-flex ga-4 flex-column flex-md-row justify-md-space-between mb-4">
            <h1 class="my-0">{{ pgettext('title', 'Diagnosis') }}</h1>

            <div class="text-center">
                <v-btn color="green" size="large" @click="submit()">
                    <strong>{{ pgettext('action', 'Continue') }}</strong>
                </v-btn>
            </div>
        </header>

        <div v-if="predefinedDiagnosisList !== null">
            <DiagnosisBuilder ref="builder"
                              :diagnosis="diagnosis"
                              :predefinedDiagnosis="predefinedDiagnosisList"/>
        </div>
        <v-empty-state v-else-if="loading">
            <template #media>
                <v-progress-circular indeterminate :size="48" :width="6" color="blue"/>
            </template>
        </v-empty-state>
        <v-empty-state v-else
                       :icon="mdiCloseCircle"
                       color="red"
                       :title="pgettext('title', 'Error while loading list of diagnoses.')"
                       :action-text="pgettext('action', 'Reload')"
                       @click:action="refetch"/>

        <v-dialog v-model="showErrorDialog" :max-width="480" closable>
            <v-card>
                <v-card-text>
                    <v-alert type="error" variant="tonal">
                        <strong>
                            {{ pgettext('error', 'Please enter the diagnosis of the patient.') }}
                        </strong>
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="showErrorDialog = false">
                        {{ pgettext('action', 'Close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </main>
</template>
